import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Check, Minus, ShoppingBag, ArrowLeft, Share2, Copy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Shareable Product Comparison Page
 * Loads comparison from URL query params
 */
export const CompareProductsPage = () => {
  const [searchParams] = useSearchParams();
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const productsParam = searchParams.get('products');
    if (productsParam) {
      const productIds = productsParam.split(',').filter(id => id);
      if (productIds.length >= 2) {
        fetchComparison(productIds);
      } else {
        setError('Please select at least 2 products to compare');
        setLoading(false);
      }
    } else {
      setError('No products specified for comparison');
      setLoading(false);
    }
  }, [searchParams]);

  const fetchComparison = async (productIds) => {
    try {
      const response = await axios.post(`${API_URL}/api/products/compare`, productIds);
      setComparisonData(response.data);
    } catch (err) {
      console.error('Failed to fetch comparison:', err);
      setError('Failed to load comparison. Some products may not exist.');
    } finally {
      setLoading(false);
    }
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    } catch (e) {
      toast.error('Failed to copy link');
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-8 bg-neutral-50">
        <div className="container-xl">
          <div className="flex items-center justify-between">
            <div>
              <Link to="/products" className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 mb-4">
                <ArrowLeft className="w-4 h-4" /> Back to Products
              </Link>
              <h1 className="text-3xl font-serif">Product Comparison</h1>
            </div>
            <Button variant="outline" onClick={copyShareLink}>
              <Share2 className="w-4 h-4 mr-2" /> Share
            </Button>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container-xl">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-neutral-500 mb-4">{error}</p>
              <Link to="/products">
                <Button>Browse Products</Button>
              </Link>
            </div>
          ) : comparisonData ? (
            <div>
              {/* Product Cards */}
              <div className={`grid gap-6 mb-12 ${
                comparisonData.products.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'
              }`}>
                {comparisonData.products.map((product) => (
                  <div key={product.id} className="border rounded-xl overflow-hidden">
                    {product.image && (
                      <div className="aspect-[4/3] bg-neutral-100">
                        <img 
                          src={product.image.startsWith('/') ? `${API_URL}${product.image}` : product.image}
                          alt={product.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <p className="text-xs text-amber-600 uppercase tracking-wider mb-1">
                        {product.category}
                      </p>
                      <h3 className="font-semibold text-xl">{product.title}</h3>
                      <p className="text-neutral-500 mt-2 line-clamp-2">
                        {product.description}
                      </p>
                      <div className="mt-4 flex items-center justify-between">
                        <span className="text-xl font-semibold text-amber-600">
                          From ${product.price_from || 0}
                        </span>
                        <Link to={product.cta_link}>
                          <Button size="sm" className="bg-amber-500 hover:bg-amber-600">
                            <ShoppingBag className="w-4 h-4 mr-1" /> Configure
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Specifications Table */}
              {comparisonData.specs.length > 0 && (
                <div className="mb-12">
                  <h2 className="text-xl font-semibold mb-6">Specifications</h2>
                  <div className="border rounded-xl overflow-hidden overflow-x-auto">
                    <table className="w-full min-w-[600px]">
                      <thead className="bg-neutral-50">
                        <tr>
                          <th className="text-left px-6 py-4 text-sm font-medium text-neutral-600 w-1/4">
                            Specification
                          </th>
                          {comparisonData.products.map((p) => (
                            <th key={p.id} className="text-center px-6 py-4 text-sm font-medium">
                              {p.title}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {comparisonData.specs.map((spec) => (
                          <tr key={spec} className="hover:bg-neutral-50">
                            <td className="px-6 py-4 text-neutral-600 capitalize">
                              {spec.replace(/_/g, ' ')}
                            </td>
                            {comparisonData.products.map((p) => (
                              <td key={p.id} className="px-6 py-4 text-center">
                                {p.specifications[spec] || (
                                  <Minus className="w-4 h-4 mx-auto text-neutral-300" />
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Features Comparison */}
              {comparisonData.features.filter(f => f).length > 0 && (
                <div className="mb-12">
                  <h2 className="text-xl font-semibold mb-6">Features</h2>
                  <div className="border rounded-xl overflow-hidden overflow-x-auto">
                    <table className="w-full min-w-[600px]">
                      <thead className="bg-neutral-50">
                        <tr>
                          <th className="text-left px-6 py-4 text-sm font-medium text-neutral-600 w-1/4">
                            Feature
                          </th>
                          {comparisonData.products.map((p) => (
                            <th key={p.id} className="text-center px-6 py-4 text-sm font-medium">
                              {p.title}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {comparisonData.features.filter(f => f).map((feature) => (
                          <tr key={feature} className="hover:bg-neutral-50">
                            <td className="px-6 py-4 text-neutral-600">
                              {feature}
                            </td>
                            {comparisonData.products.map((p) => (
                              <td key={p.id} className="px-6 py-4 text-center">
                                {p.features[feature] ? (
                                  <Check className="w-5 h-5 mx-auto text-green-500" />
                                ) : (
                                  <Minus className="w-4 h-4 mx-auto text-neutral-300" />
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Key Benefits */}
              <div>
                <h2 className="text-xl font-semibold mb-6">Key Benefits</h2>
                <div className={`grid gap-6 ${
                  comparisonData.products.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'
                }`}>
                  {comparisonData.products.map((p) => (
                    <div key={p.id} className="border rounded-xl p-6">
                      <h3 className="font-semibold mb-4">{p.title}</h3>
                      <ul className="space-y-3">
                        {(p.benefits || []).map((benefit, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                            <span>{typeof benefit === 'string' ? benefit : (benefit.title || benefit.text || '')}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </Layout>
  );
};

export default CompareProductsPage;
